import { useContext, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { IoIosNotificationsOutline } from "react-icons/io";
import { IoMdMenu } from "react-icons/io";
import { MdOutlineRoomService } from "react-icons/md";
import { PiUserCircleLight } from "react-icons/pi";
import { FiUser } from "react-icons/fi";
import SideBar from "../components/SideBar";
import { FoodServiceContext } from "../context/FoodServiceContext";
import { UserContext } from "../context/UserContext";
import { useClickOutside } from "../hooks/useClickOutside";
import { formatPersianNumber as toPersianNumber } from "../utils/persianNumber";

const SIDEBAR_PREFERENCE_KEY = "mizbanDashboardSidebarCollapsed";

const getInitialSidebarState = () => {
  const storedPreference = localStorage.getItem(SIDEBAR_PREFERENCE_KEY);
  if (storedPreference !== null) return storedPreference === "true";
  return window.matchMedia("(min-width: 768px) and (max-width: 1199px)").matches;
};

export default function Layout() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(getInitialSidebarState);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [progress, setProgress] = useState(0);

  const { user, logoutUser } = useContext(UserContext);
  const { restaurantData, orders, newOrder, setHasNewOrder } = useContext(FoodServiceContext);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const profileButtonRef = useRef(null);
  const notificationRef = useRef(null);

  const pendingOrders = orders.filter((order) => order.status === "pending");
  const pendingCount = pendingOrders.length;
  const latestPendingOrder = pendingOrders[0];
  const restaurantName = restaurantData?.name || user?.name || "رستوران";

  useEffect(() => {
    localStorage.setItem(SIDEBAR_PREFERENCE_KEY, String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;
      setIsMobileNavOpen(false);
      setOpenUserMenu(false);
      setShowNotifications(false);
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  useClickOutside([menuRef, profileButtonRef], () => setOpenUserMenu(false));
  useClickOutside(notificationRef, () => setShowNotifications(false));

  useEffect(() => {
    if (!newOrder) return;
    setShowNotifications(true);
    setProgress(0);
    setHasNewOrder(false);
  }, [newOrder, setHasNewOrder]);

  useEffect(() => {
    if (!showNotifications) return undefined;

    setProgress(0);
    const timeout = setTimeout(() => setShowNotifications(false), 30000);
    const progressTimer = setTimeout(() => setProgress(100), 50);

    return () => {
      clearTimeout(timeout);
      clearTimeout(progressTimer);
      setProgress(0);
    };
  }, [showNotifications]);

  const handleLogout = () => {
    logoutUser();
    navigate("/auth/restaurant", { replace: true });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F4F6F5]" dir="rtl">
      <SideBar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((current) => !current)}
        isMobileOpen={isMobileNavOpen}
        onCloseMobile={() => setIsMobileNavOpen(false)}
      />

      <main
        className={`flex min-w-0 flex-1 flex-col transition-[margin] duration-200 ${
          isSidebarCollapsed ? "md:mr-20" : "md:mr-64"
        }`}
      >
        <header className="relative z-20 flex h-16 shrink-0 items-center justify-between bg-[#16302A] px-3 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setIsMobileNavOpen(true)}
              className="rounded-lg p-2 text-2xl text-white transition-colors hover:bg-[#1C3B33] focus:outline-none focus:ring-2 focus:ring-primary/30 md:hidden"
              aria-label="باز کردن منوی ناوبری"
              aria-controls="mobile-dashboard-navigation"
              aria-expanded={isMobileNavOpen}
            >
              <IoMdMenu aria-hidden="true" />
            </button>

            <div className="min-w-0 leading-tight">
              <p className="truncate text-sm font-bold text-white sm:text-base">سلام، {restaurantName}</p>
              {pendingCount > 0 && (
                <p className="mt-1 hidden text-xs text-[#A9C2BA] sm:block">
                  {toPersianNumber(pendingCount)} سفارش در انتظار رسیدگی
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard/orders")}
              className={`inline-flex min-h-10 items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-4 sm:px-4 ${
                pendingCount > 0
                  ? "bg-primary text-white hover:bg-[#e8731f] focus:ring-primary/20"
                  : "border border-white/10 bg-[#1C3B33] text-[#A9C2BA] hover:bg-[#21453C] hover:text-white focus:ring-white/10"
              }`}
            >
              <MdOutlineRoomService className="text-lg" aria-hidden="true" />
              <span className="hidden sm:inline">سفارش‌های جدید</span>
              <span aria-label={`${pendingCount} سفارش جدید`}>({toPersianNumber(pendingCount)})</span>
            </button>

            <div className="relative" ref={notificationRef}>
              <button
                type="button"
                onClick={() => setShowNotifications((current) => !current)}
                className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-[#1C3B33] text-xl text-[#A9C2BA] transition-colors hover:bg-[#21453C] hover:text-white focus:outline-none focus:ring-4 focus:ring-white/10"
                aria-label="اعلان‌های سفارش"
                aria-expanded={showNotifications}
                aria-haspopup="dialog"
              >
                <IoIosNotificationsOutline aria-hidden="true" />
                {pendingCount > 0 && (
                  <span className="absolute -left-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                    {toPersianNumber(pendingCount)}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div
                  role="dialog"
                  aria-label="آخرین اعلان سفارش"
                  className="absolute left-0 top-12 z-50 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-card"
                >
                  <div className="h-1 bg-gray-100">
                    <div
                      className="h-full bg-primary transition-all duration-[30000ms] ease-linear"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="p-4">
                    {latestPendingOrder ? (
                      <>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-accent">سفارش جدید دریافت شده است</p>
                            <p className="mt-1 text-xs text-gray-500">
                              میز: {latestPendingOrder.tableNumber || "بیرون‌بر"}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowNotifications(false)}
                            className="rounded-md px-1.5 py-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-200"
                            aria-label="بستن اعلان"
                          >
                            ×
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          جمع کل: {toPersianNumber(latestPendingOrder.totalPrice)} تومان
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNotifications(false);
                            navigate("/dashboard/orders");
                          }}
                          className="mt-4 w-full rounded-lg bg-secondary_1 px-3 py-2 text-xs font-semibold text-secondary hover:bg-[#dcefeb] focus:outline-none focus:ring-2 focus:ring-secondary/20"
                        >
                          مشاهده سفارش‌ها
                        </button>
                      </>
                    ) : (
                      <p className="text-center text-sm text-gray-500">اعلان سفارش جدیدی ندارید.</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {user && (
              <div className="relative flex items-center text-accent">
                <button
                  ref={profileButtonRef}
                  type="button"
                  onClick={() => setOpenUserMenu((current) => !current)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-3xl text-[#A9C2BA] transition-colors hover:bg-[#1C3B33] hover:text-white focus:outline-none focus:ring-4 focus:ring-white/10"
                  aria-label="نمایش اطلاعات حساب"
                  aria-expanded={openUserMenu}
                  aria-haspopup="menu"
                >
                  <PiUserCircleLight aria-hidden="true" />
                </button>

                {openUserMenu && (
                  <div ref={menuRef} role="menu" className="absolute left-0 top-12 z-50 w-72 rounded-2xl border border-gray-200 bg-white p-4 shadow-card">
                    <div className="mb-4 flex items-center gap-3 border-b border-gray-100 pb-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary_1 text-secondary">
                        <PiUserCircleLight className="text-3xl" aria-hidden="true" />
                      </div>
                      <div className="min-w-0 leading-tight">
                        <p className="truncate text-sm font-semibold text-accent">{restaurantName}</p>
                        <p className="mt-1 text-xs text-gray-500">حساب رستوران</p>
                      </div>
                    </div>

                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between gap-3 text-gray-600">
                        <span>ایمیل</span>
                        <span className="max-w-[160px] truncate font-medium text-accent" dir="ltr">{user.email || "—"}</span>
                      </div>
                      <div className="flex justify-between gap-3 text-gray-600">
                        <span>شماره تماس</span>
                        <span className="font-medium text-accent" dir="ltr">{user.phone || "—"}</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setOpenUserMenu(false); navigate(`/dashboard/profile/${user._id}`); }}
                      className="mt-4 flex w-full items-center gap-2.5 rounded-xl px-2 py-2.5 text-sm font-medium text-accent transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                      <FiUser className="text-base" aria-hidden="true" />
                      پروفایل رستوران
                    </button>

                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleLogout}
                      className="mt-1 flex w-full items-center justify-center rounded-xl py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200"
                    >
                      خروج از حساب
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </header>

        <div className="mx-auto w-full max-w-[1600px] flex-1 overflow-y-auto bg-[#F4F6F5] px-3 py-4 sm:px-4 sm:py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
